import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CalendarDays, ChevronDown, Clock, MessageCircle, UserRound } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { learningTopicDetails } from "@/data/publicFoundation";
import { localizeLearningTopicDetail, rtlLearningLanguages } from "@/components/public/learningTopicTranslations";
import { getLearningArticlePageCopy, getLocalizedFallbackFaqs, getLocalizedLearningArticle } from "@/lib/learningArticlePageCopy";
import { adaptLearningArticle, adaptLearningArticles } from "@/lib/publicLearningArticleAdapter";
import PublicBreadcrumbs from "@/components/public/PublicBreadcrumbs";
import PublicReadingProgressBar from "@/components/public/PublicReadingProgressBar";

const LearningStructuredData = React.lazy(() => import("@/components/public/LearningStructuredData"));
const SocialShare = React.lazy(() => import("@/components/learning/SocialShare"));
const AdMarkdownBody = React.lazy(() => import("@/components/ads/AdMarkdownBody"));
const ArticleLeaderboardAd = React.lazy(() => import("@/components/ads/ArticleLeaderboardAd"));
const ArticleSidebarAd = React.lazy(() => import("@/components/ads/ArticleSidebarAd"));
const ArticleTrustSignals = React.lazy(() => import("@/components/public/ArticleTrustSignals"));
const KeyTakeawaysBox = React.lazy(() => import("@/components/public/KeyTakeawaysBox"));
import { ensureMinimumLearningArticleMarkdown } from "@/lib/minimumLearningArticleContent";
import { SUPPORTED_LANGS, articleMatchesLearningSlug, localizedLearningArticleUrl } from "@/lib/learningUrls";

const SITE_URL = "https://marstick.com";
const DEFAULT_LEARNING_IMAGE = "/icons/marstick-icon-512.png";
const sanitizeSeoImage = (value) => /base44/i.test(String(value || "")) ? `${SITE_URL}/icons/marstick-icon-512.png` : value;

const formatTopicLabel = (value = "") => String(value || "")
  .split("-")
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(" ");

const keyTakeawayLabels = {
  en: "Key takeaways",
  he: "עיקרי הדברים",
  ar: "أهم النقاط",
  es: "Puntos clave",
  it: "Punti chiave",
  fr: "Points clés",
  de: "Wichtige Erkenntnisse",
  pt: "Principais pontos",
  el: "Βασικά σημεία",
  ru: "Ключевые выводы",
  tr: "Öne çıkanlar",
  zh: "关键要点",
};

const readMoreLabels = {
  en: { heading: "Read More", subtitle: "Continue with related sailing guides and topic pages to build a stronger understanding before your next passage.", topicHub: "Explore more in", allGuides: "Browse the Learning Center", allGuidesText: "Find navigation, weather, seamanship, safety, and marina planning articles." },
  he: { heading: "קראו עוד", subtitle: "המשיכו למדריכי שייט ודפי נושא קשורים כדי לבנות הבנה טובה יותר לפני ההפלגה הבאה.", topicHub: "גלו עוד בנושא", allGuides: "עיינו במרכז הלימוד", allGuidesText: "מצאו מאמרים על ניווט, מזג אוויר, ימאות, בטיחות ותכנון מרינות." },
  ar: { heading: "اقرأ المزيد", subtitle: "تابع مع أدلة إبحار وصفحات مواضيع مرتبطة لبناء فهم أقوى قبل رحلتك التالية.", topicHub: "استكشف المزيد في", allGuides: "تصفح مركز التعلم", allGuidesText: "ابحث عن مقالات الملاحة والطقس وفنون البحر والسلامة وتخطيط المراسي." },
  es: { heading: "Leer más", subtitle: "Continúa con guías de navegación y páginas relacionadas para comprender mejor antes de tu próxima travesía.", topicHub: "Explorar más en", allGuides: "Ver el Centro de aprendizaje", allGuidesText: "Encuentra artículos de navegación, meteorología, maniobra, seguridad y planificación de marinas." },
  it: { heading: "Leggi altro", subtitle: "Continua con guide di vela e pagine correlate per prepararti meglio al prossimo passaggio.", topicHub: "Esplora altro in", allGuides: "Sfoglia il Centro apprendimento", allGuidesText: "Trova articoli su navigazione, meteo, marineria, sicurezza e pianificazione marine." },
  fr: { heading: "Lire aussi", subtitle: "Continuez avec des guides de navigation et pages liées pour mieux préparer votre prochaine traversée.", topicHub: "Explorer plus dans", allGuides: "Parcourir le centre d’apprentissage", allGuidesText: "Trouvez des articles sur navigation, météo, matelotage, sécurité et marinas." },
  de: { heading: "Mehr lesen", subtitle: "Lesen Sie verwandte Segelguides und Themenseiten, um sich besser auf den nächsten Törn vorzubereiten.", topicHub: "Mehr entdecken in", allGuides: "Lernzentrum öffnen", allGuidesText: "Finden Sie Artikel zu Navigation, Wetter, Seemannschaft, Sicherheit und Marina-Planung." },
  pt: { heading: "Leia mais", subtitle: "Continue com guias de navegação e páginas relacionadas para entender melhor antes da próxima viagem.", topicHub: "Explore mais em", allGuides: "Ver Centro de aprendizagem", allGuidesText: "Encontre artigos sobre navegação, meteorologia, marinharia, segurança e marinas." },
  el: { heading: "Διαβάστε περισσότερα", subtitle: "Συνεχίστε με σχετικούς οδηγούς και σελίδες θεμάτων πριν από το επόμενο πέρασμα.", topicHub: "Εξερευνήστε περισσότερα στο", allGuides: "Άνοιγμα κέντρου μάθησης", allGuidesText: "Βρείτε άρθρα για πλοήγηση, καιρό, ναυτοσύνη, ασφάλεια και μαρίνες." },
  ru: { heading: "Читайте также", subtitle: "Продолжайте с похожими гайдами и тематическими страницами перед следующим переходом.", topicHub: "Еще по теме", allGuides: "Открыть учебный центр", allGuidesText: "Статьи о навигации, погоде, морской практике, безопасности и маринах." },
  tr: { heading: "Daha fazla oku", subtitle: "Sonraki seyirden önce ilgili rehberler ve konu sayfalarıyla bilginizi güçlendirin.", topicHub: "Daha fazlası", allGuides: "Öğrenme merkezini aç", allGuidesText: "Navigasyon, hava, denizcilik, güvenlik ve marina planlama makaleleri." },
  zh: { heading: "继续阅读", subtitle: "继续浏览相关航海指南和主题页面，为下一次航行建立更扎实的理解。", topicHub: "探索更多", allGuides: "浏览学习中心", allGuidesText: "查找导航、天气、航海技能、安全和码头规划文章。" },
};

const articleImageAltLabels = {
  en: "featured sailing guide image",
  he: "תמונת מדריך שייט ראשית",
  es: "imagen destacada de guía náutica",
  it: "immagine principale della guida nautica",
  fr: "image principale du guide nautique",
  de: "Titelbild des Segelguides",
  pt: "imagem principal do guia náutico",
  ar: "صورة دليل الإبحار الرئيسية",
  el: "κύρια εικόνα οδηγού ιστιοπλοΐας",
  ru: "главное изображение морского руководства",
  tr: "öne çıkan yelken rehberi görseli",
  zh: "航海指南主图",
};

const formatDate = (value, lang = "en", recent = "Recently updated") => {
  if (!value) return recent;
  return new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
};

const createAnchorId = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .trim()
    .replace(/\s+/g, "-");

const headingText = (children) => React.Children.toArray(children).join("");

const upsertHeadTag = (selector, tagName, attributes) => {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement(tagName);
    document.head.appendChild(tag);
  }
  Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
};

const fallbackFaqs = [
  { q: "Who is this guide for?", a: "This guide is designed for sailors, skippers, and boating learners who want practical, easy-to-follow maritime guidance." },
  { q: "Can I use this as my only navigation source?", a: "No. Use this article for learning and planning, then verify decisions with official charts, forecasts, and local notices." },
  { q: "How should I apply this onboard?", a: "Start with the key steps, discuss them with your crew, and adapt them to your boat, route, weather, and local rules." },
  { q: "Is this useful for beginners?", a: "Yes. The explanations are written to be clear for beginners while still being useful as a refresher for experienced sailors." },
  { q: "How often should I review this topic?", a: "Review it before relevant trips, training sessions, or any passage where the topic affects safety or decision-making." },
];

const normalizeArticleContent = (content = "") => {
  if (!content) return "";

  const raw = String(content || "");
  const hasEscapedMarkdown = /\\[#*\-.]|\\#{2,6}|\\-\s|\d+\\\.\s/.test(raw);
  const hasInlineMarkdown = /\S\s+#{2,6}\s/.test(raw) || /\S\s+-\s+\*\*/.test(raw) || /\S\s+\d+\.\s+\*\*/.test(raw);
  const cleaned = hasEscapedMarkdown || hasInlineMarkdown
    ? raw
      .replace(/\s+(\\#{2,6}\s+)/g, "\n\n$1")
      .replace(/\s+(\\-\s+)/g, "\n$1")
      .replace(/\s+(\d+\\\.\s+)/g, "\n$1")
      .replace(/\\([#*\-_.!`[\]()])/g, "$1")
      .replace(/\s+(#{2,6}\s+)/g, "\n\n$1")
      .replace(/([.:!?])\s+(-\s+)/g, "$1\n$2")
      .replace(/\s+(-\s+\*\*)/g, "\n$1")
      .replace(/\s+(\d+\.\s+\*\*)/g, "\n$1")
    : raw;

  return cleaned
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const normalizedLine = /^#\s+/.test(line) ? line.replace(/^#\s+/, "## ") : line;
      const longHeading = normalizedLine.match(/^(#{2,3}\s+)(.{36,}?[?:.!])\s+(.{24,})$/u);
      if (longHeading) return `${longHeading[1]}${longHeading[2]}\n\n${longHeading[3]}`;
      return normalizedLine;
    })
    .join("\n\n");
};

const takeawayHeadingPattern = /^##\s+(key takeaways|takeaways|quick takeaways|main takeaways)\s*$/i;

const extractKeyTakeaways = (markdown = "", fallbackText = "", sections = []) => {
  const lines = String(markdown || "").split("\n");
  const start = lines.findIndex((line) => takeawayHeadingPattern.test(line.trim()));
  if (start >= 0) {
    const items = [];
    for (let index = start + 1; index < lines.length; index += 1) {
      const line = lines[index].trim();
      if (/^##\s+/.test(line)) break;
      const bullet = line.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, "").trim();
      if (bullet && !/^#{1,6}\s+/.test(bullet)) items.push(bullet);
    }
    if (items.length) return items.slice(0, 5);
  }

  return [fallbackText, ...sections.map((section) => section.title)].filter(Boolean).slice(0, 4);
};

const removeKeyTakeawaysSection = (markdown = "") => {
  const lines = String(markdown || "").split("\n");
  const start = lines.findIndex((line) => takeawayHeadingPattern.test(line.trim()));
  if (start < 0) return markdown;
  let end = lines.length;
  for (let index = start + 1; index < lines.length; index += 1) {
    if (/^##\s+/.test(lines[index].trim())) {
      end = index;
      break;
    }
  }
  return [...lines.slice(0, start), ...lines.slice(end)].join("\n").trim();
};

export default function PublicLearningArticlePage() {
  const { lang = "en", topic, articleSlug } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isAdminPreview = searchParams.get("preview") === "admin";
  const previewArticleId = searchParams.get("id");
  const isRtl = rtlLearningLanguages.has(lang);
  const prefix = `/${lang}`;
  const sourceTopicData = learningTopicDetails[topic];
  const topicData = sourceTopicData
    ? localizeLearningTopicDetail(topic, sourceTopicData, lang)
    : topic
      ? {
        h1: formatTopicLabel(topic),
        intro: "Practical maritime learning guidance for safer planning, navigation, seamanship, and onboard decisions.",
        image: DEFAULT_LEARNING_IMAGE,
      }
      : null;
  const pageCopy = getLearningArticlePageCopy(lang);
  const [readingProgress, setReadingProgress] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [secondaryReady, setSecondaryReady] = useState(false);

  useEffect(() => {
    let frame = 0;
    const updateProgress = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        setReadingProgress(scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0);
      });
    };

    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, [articleSlug]);

  useEffect(() => {
    setSecondaryReady(false);
    const show = () => setSecondaryReady(true);
    const idleId = window.requestIdleCallback ? window.requestIdleCallback(show, { timeout: 2600 }) : window.setTimeout(show, 1800);
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, [articleSlug]);

  const { data: article, isLoading } = useQuery({
    queryKey: ["public-learning-article", lang, topic, articleSlug, isAdminPreview, previewArticleId],
    queryFn: async () => {
      if (isAdminPreview && previewArticleId) {
        const record = await base44.entities.LearningContent.get(previewArticleId);
        return record ? adaptLearningArticle(record) : null;
      }

      const exactFilter = isAdminPreview
        ? { main_category: topic, slug: articleSlug }
        : { main_category: topic, slug: articleSlug, is_published: true };
      const exactRecords = await base44.entities.LearningContent.filter(exactFilter, "-updated_date", 1);
      if (exactRecords[0]) return adaptLearningArticle(exactRecords[0]);

      const categoryFilter = isAdminPreview
        ? { main_category: topic }
        : { main_category: topic, is_published: true };
      const categoryRecords = await base44.entities.LearningContent.filter(categoryFilter, "-updated_date", 100);
      const localizedMatch = categoryRecords.find((record) => articleMatchesLearningSlug(record, articleSlug, lang));
      const fallbackRecord = localizedMatch || categoryRecords[0];
      return fallbackRecord ? adaptLearningArticle(fallbackRecord) : null;
    },
    enabled: !!topic && !!articleSlug,
  });

  const { data: relatedArticles = [] } = useQuery({
    queryKey: ["public-learning-related", topic, articleSlug],
    queryFn: () => base44.entities.LearningContent.filter({ main_category: topic, is_published: true }, "title", 6),
    enabled: secondaryReady && !!topic && !!article,
    select: (items) => adaptLearningArticles(items).filter((item) => item.slug !== article?.slug).slice(0, 3),
  });

  const localizedArticle = useMemo(() => getLocalizedLearningArticle(article, lang), [article, lang]);
  const articleUrl = useMemo(() => article
    ? localizedLearningArticleUrl({ lang, category: article.main_category || topic, article })
    : `${prefix}/learning-center/${topic || ""}/${articleSlug || ""}`,
    [article, articleSlug, lang, prefix, topic]
  );
  const articleMarkdown = useMemo(() => normalizeArticleContent(localizedArticle?.content), [localizedArticle?.content]);
  const canonicalUrl = `${SITE_URL}${articleUrl}`;

  useEffect(() => {
    if (!localizedArticle) return;
    const title = `${localizedArticle.title} | Marstick`;
    const description = localizedArticle.meta_description || localizedArticle.excerpt || pageCopy.loading;
    const image = sanitizeSeoImage(localizedArticle.image_urls?.[0] || topicData?.image);
    document.title = title;
    upsertHeadTag('meta[name="description"]', "meta", { name: "description", content: description });
    upsertHeadTag('meta[property="og:type"]', "meta", { property: "og:type", content: "article" });
    upsertHeadTag('meta[property="og:title"]', "meta", { property: "og:title", content: title });
    upsertHeadTag('meta[property="og:description"]', "meta", { property: "og:description", content: description });
    upsertHeadTag('meta[property="og:url"]', "meta", { property: "og:url", content: canonicalUrl });
    upsertHeadTag('link[rel="canonical"]', "link", { rel: "canonical", href: canonicalUrl });
    SUPPORTED_LANGS.forEach((language) => {
      upsertHeadTag(`link[rel="alternate"][hreflang="${language}"]`, "link", {
        rel: "alternate",
        hreflang: language,
        href: `${SITE_URL}${localizedLearningArticleUrl({ lang: language, category: article.main_category || topic, article })}`,
      });
    });
    upsertHeadTag('link[rel="alternate"][hreflang="x-default"]', "link", {
      rel: "alternate",
      hreflang: "x-default",
      href: `${SITE_URL}${localizedLearningArticleUrl({ lang: "en", category: article.main_category || topic, article })}`,
    });
    upsertHeadTag('meta[property="article:section"]', "meta", { property: "article:section", content: topicData?.h1 || topic });
    upsertHeadTag('meta[property="article:author"]', "meta", { property: "article:author", content: "Marstick Editorial Team" });
    if (localizedArticle.created_date) upsertHeadTag('meta[property="article:published_time"]', "meta", { property: "article:published_time", content: localizedArticle.created_date });
    if (localizedArticle.updated_date || localizedArticle.created_date) upsertHeadTag('meta[property="article:modified_time"]', "meta", { property: "article:modified_time", content: localizedArticle.updated_date || localizedArticle.created_date });
    if (localizedArticle.keywords?.length) upsertHeadTag('meta[name="keywords"]', "meta", { name: "keywords", content: localizedArticle.keywords.slice(0, 12).join(", ") });
    if (image) upsertHeadTag('meta[property="og:image"]', "meta", { property: "og:image", content: image });
    upsertHeadTag('meta[name="twitter:card"]', "meta", { name: "twitter:card", content: "summary_large_image" });
    upsertHeadTag('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: title });
    upsertHeadTag('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: description });
    if (image) upsertHeadTag('meta[name="twitter:image"]', "meta", { name: "twitter:image", content: image });
  }, [article, localizedArticle, canonicalUrl, lang, pageCopy.loading, topic, topicData?.image]);

  const sections = useMemo(() => {
    if (!articleMarkdown) return [];
    return articleMarkdown
      .split("\n")
      .filter((line) => /^#{2,3}\s+/.test(line))
      .map((line) => {
        const title = line.replace(/^#{2,3}\s+/, "").trim();
        return { title, id: createAnchorId(title) };
      })
      .filter((section) => !takeawayHeadingPattern.test(`## ${section.title}`))
      .slice(0, 8);
  }, [articleMarkdown]);

  const keyTakeaways = useMemo(
    () => extractKeyTakeaways(articleMarkdown, localizedArticle?.meta_description || localizedArticle?.excerpt, sections),
    [articleMarkdown, localizedArticle?.meta_description, localizedArticle?.excerpt, sections]
  );
  const bodyMarkdown = useMemo(() => ensureMinimumLearningArticleMarkdown(
    removeKeyTakeawaysSection(articleMarkdown),
    {
      lang,
      title: localizedArticle?.title,
      topic: topicData?.h1,
      description: localizedArticle?.meta_description || localizedArticle?.excerpt,
    }
  ), [articleMarkdown, lang, localizedArticle?.title, localizedArticle?.meta_description, localizedArticle?.excerpt, topicData?.h1]);
  const originalWordCount = useMemo(() => String(articleMarkdown || "").split(/\s+/).filter(Boolean).length, [articleMarkdown]);

  useEffect(() => {
    if (!localizedArticle) return;
    if (originalWordCount > 0 && originalWordCount < 450) {
      upsertHeadTag('meta[name="robots"]', "meta", { name: "robots", content: "noindex, nofollow" });
      upsertHeadTag('meta[name="googlebot"]', "meta", { name: "googlebot", content: "noindex, nofollow" });
    }
  }, [localizedArticle, originalWordCount]);

  if (isLoading) {
    return <div className="maritime-container py-12 text-muted-foreground">{pageCopy.loading}</div>;
  }

  if (!topicData || !article) {
    return <Navigate to={`${prefix}/learning-center${topic ? `/${topic}` : ""}`} replace />;
  }

  const displayFaqs = [...(localizedArticle.faq || []), ...getLocalizedFallbackFaqs(lang, fallbackFaqs)].slice(0, 5);
  const readMoreCopy = readMoreLabels[lang];
  const articleImageAlt = articleImageAltLabels[lang];

  if (!readMoreCopy || !articleImageAlt) return null;
  const readMoreCards = [
    ...relatedArticles.map((item) => {
      const localizedRelated = getLocalizedLearningArticle(item, lang);
      return {
        id: item.slug,
        href: localizedLearningArticleUrl({ lang, category: topic, article: item }),
        eyebrow: item.subtopic || topicData.h1,
        title: localizedRelated.title,
        excerpt: localizedRelated.excerpt || localizedRelated.meta_description,
      };
    }),
    {
      id: `${topic}-topic-hub`,
      href: `${prefix}/learning-center/${topic}`,
      eyebrow: topicData.h1,
      title: `${readMoreCopy.topicHub} ${topicData.h1}`,
      excerpt: topicData.intro,
    },
    {
      id: "learning-center",
      href: `${prefix}/learning-center`,
      eyebrow: pageCopy.learningCenter,
      title: readMoreCopy.allGuides,
      excerpt: readMoreCopy.allGuidesText,
    },
  ].filter((item) => item.title && item.href).slice(0, 5);

  const breadcrumbs = [
    { name: pageCopy.home, href: prefix },
    { name: pageCopy.learningCenter, href: `${prefix}/learning-center` },
    { name: topicData.h1, href: `${prefix}/learning-center/${topic}` },
    { name: localizedArticle.title, href: articleUrl },
  ];

  return (
    <article
      dir={isRtl ? "rtl" : "ltr"}
      className="learning-article-page min-h-screen bg-[#EAFBFC] py-8 md:py-10"
    >
      <div className="maritime-container">
      <PublicReadingProgressBar progress={readingProgress} />
      <React.Suspense fallback={null}>
        <LearningStructuredData article={localizedArticle} topic={topicData} url={articleUrl} breadcrumbs={breadcrumbs} faq={displayFaqs} />
      </React.Suspense>

      <PublicBreadcrumbs
        items={[
          { label: pageCopy.home, href: prefix },
          { label: pageCopy.learningCenter, href: `${prefix}/learning-center` },
          { label: topicData.h1, href: `${prefix}/learning-center/${topic}` },
          { label: localizedArticle.title },
        ]}
      />

      <header className="overflow-hidden rounded-2xl border border-[#BFDADC] bg-white shadow-[0_10px_24px_rgba(8,22,51,0.08)]">
        <div className="relative min-h-[14rem] overflow-hidden bg-[#081633] text-white md:min-h-[17rem]">
          <img src={localizedArticle.image_urls?.[0] || topicData.image} alt={`${localizedArticle.title} ${articleImageAlt}`} className="absolute inset-0 h-full w-full object-cover" width="1600" height="900" sizes="100vw" loading="eager" decoding="async" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#081633]/92 via-[#081633]/68 to-[#17A6A4]/22" />
          <div className="relative flex min-h-[14rem] flex-col justify-center p-5 md:min-h-[17rem] md:p-8">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-[#9CE7E6]">{topicData.h1}</p>
            <h1 className="mt-3 max-w-4xl font-heading text-3xl font-black leading-tight tracking-[-0.035em] md:text-5xl">{localizedArticle.title}</h1>
            <p className="mt-3 max-w-3xl text-base leading-7 text-white/80">{localizedArticle.meta_description || localizedArticle.excerpt}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm font-bold text-white/80">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 backdrop-blur"><UserRound className="h-4 w-4" /> {pageCopy.author}</span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 backdrop-blur"><Clock className="h-4 w-4" /> {localizedArticle.reading_time_minutes || 5} {pageCopy.minRead}</span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 backdrop-blur"><CalendarDays className="h-4 w-4" /> {pageCopy.published} {formatDate(localizedArticle.created_date, lang, pageCopy.recent)}</span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 backdrop-blur"><CalendarDays className="h-4 w-4" /> {pageCopy.updated} {formatDate(localizedArticle.updated_date || localizedArticle.created_date, lang, pageCopy.recent)}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-6">
          <div className="rounded-2xl border border-[#BFDADC] bg-white p-5 shadow-[0_8px_20px_rgba(8,22,51,0.06)] md:p-7">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#BFDADC] pb-4">
              <Button asChild variant="outline" size="sm"><Link to={`${prefix}/learning-center/${topic}`}><ArrowLeft className="h-4 w-4" /> {pageCopy.backTopic}</Link></Button>
              <React.Suspense fallback={<div className="h-10 w-28" aria-hidden="true" />}>
                <SocialShare title={localizedArticle.title} url={`https://marstick.com${articleUrl}`} label={pageCopy.share} />
              </React.Suspense>
            </div>
            <React.Suspense fallback={<div className="mb-5 min-h-[8rem] rounded-xl border border-[#BFDADC] bg-[#EAFBFC]/60" aria-hidden="true" />}>
              <KeyTakeawaysBox title={keyTakeawayLabels[lang]} items={keyTakeaways} />
            </React.Suspense>
            <div data-article-intro className="mb-5 rounded-xl border border-[#BFDADC] bg-[#EAFBFC]/60 p-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#168F92]">{pageCopy.articleTopic}</p>
              <h2 className="mt-2 font-heading text-2xl font-black text-[#081633] md:text-[1.7rem]">{topicData.h1}</h2>
              <p className="mt-3 max-w-3xl text-base font-medium leading-7 text-[#10233E]">{localizedArticle.excerpt || localizedArticle.meta_description}</p>
            </div>
            <React.Suspense fallback={<div className="my-6 min-h-[120px] rounded-2xl border border-[#BFDADC] bg-[#EAFBFC]/60" aria-hidden="true" />}>
              <ArticleLeaderboardAd className="my-6" />
            </React.Suspense>
            <React.Suspense fallback={<div className="mx-auto min-h-[34rem] max-w-[720px] rounded-2xl bg-[#EAFBFC]/60" aria-hidden="true" />}>
              <AdMarkdownBody
                markdown={bodyMarkdown}
                className="prose prose-base prose-slate mx-auto max-w-[720px] prose-headings:scroll-mt-24 prose-headings:font-heading prose-headings:font-black prose-headings:text-[#081633] prose-h2:mt-8 prose-h2:mb-3 prose-h2:border-b prose-h2:border-[#BFDADC] prose-h2:pb-2 prose-h2:text-2xl prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-xl prose-p:my-3 prose-p:text-base prose-p:leading-7 prose-p:text-[#10233E] prose-li:my-1 prose-li:leading-7 prose-ul:my-4 prose-ol:my-4 prose-strong:text-[#081633] prose-a:text-[#168F92]"
                components={{
                  h2: ({ children }) => <h2 id={createAnchorId(headingText(children))}>{children}</h2>,
                  h3: ({ children }) => <h3 id={createAnchorId(headingText(children))}>{children}</h3>,
                }}
              />
            </React.Suspense>
          </div>

          <section className="rounded-[2rem] border border-[#BFDADC] bg-white/90 p-6 shadow-[0_14px_35px_rgba(8,22,51,0.08)] md:p-10">
            <h2 className="font-heading text-3xl font-black text-[#081633]">{pageCopy.faq}</h2>
            <div className="mt-6 overflow-hidden rounded-3xl border border-[#BFDADC] bg-white shadow-sm">
              {displayFaqs.map((item, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div key={item.q} className="border-b border-border last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-muted/45 md:px-6"
                    >
                      <span className="font-black text-foreground">{item.q}</span>
                      <ChevronDown className={`h-5 w-5 shrink-0 text-primary transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="border-t border-border bg-background/70 px-5 py-5 md:px-6">
                        <p className="max-w-3xl leading-8 text-muted-foreground">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-[2rem] border border-dashed border-[#BFDADC] bg-white/80 p-6 text-center md:p-8">
            <MessageCircle className="mx-auto h-7 w-7 text-primary" />
            <h2 className="mt-3 font-heading text-2xl font-black">{pageCopy.commentsTitle}</h2>
            <p className="mt-2 text-muted-foreground">{pageCopy.commentsText}</p>
          </section>

          <section className="rounded-[2rem] border border-[#BFDADC] bg-white/95 p-6 shadow-[0_14px_35px_rgba(8,22,51,0.08)] md:p-10">
            <div className="max-w-3xl">
              <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{pageCopy.related}</p>
              <h2 className="mt-2 font-heading text-3xl font-black text-[#081633] md:text-4xl">{readMoreCopy.heading}</h2>
              <p className="mt-3 text-base leading-7 text-muted-foreground">{readMoreCopy.subtitle}</p>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {readMoreCards.map((item) => (
                <Link key={item.id} to={item.href} className="group rounded-3xl border border-[#BFDADC] bg-[#EAFBFC]/70 p-5 transition hover:border-primary/60 hover:bg-white hover:shadow-lg">
                  <p className="text-xs font-black uppercase tracking-widest text-primary">{item.eyebrow}</p>
                  <h3 className="mt-3 font-heading text-lg font-black leading-tight text-[#081633]">{item.title}</h3>
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-muted-foreground">{item.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-black text-primary">{readMoreCopy.heading} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </Link>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
          <React.Suspense fallback={<div className="min-h-[280px] rounded-[1.5rem] border border-[#BFDADC] bg-white/70" aria-hidden="true" />}>
            <ArticleSidebarAd />
          </React.Suspense>
          <React.Suspense fallback={<div className="min-h-[220px] rounded-[1.5rem] border border-[#BFDADC] bg-white/70" aria-hidden="true" />}>
            <ArticleTrustSignals
              variant="compact"
              author={pageCopy.author}
              updatedDate={localizedArticle.updated_date || localizedArticle.created_date}
              category={topicData.h1}
              readingTime={`${localizedArticle.reading_time_minutes || 5} ${pageCopy.minRead}`}
              lang={lang}
            />
          </React.Suspense>

          <div className="rounded-[1.5rem] border border-[#BFDADC] bg-white/92 p-5 shadow-[0_14px_35px_rgba(8,22,51,0.08)]">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{pageCopy.toc}</p>
            <div className="mt-4 space-y-2">
              {sections.length ? sections.map((section) => (
                <a key={section.id} href={`#${section.id}`} className="block rounded-2xl px-3 py-2 text-sm font-bold text-muted-foreground transition hover:bg-primary/10 hover:text-primary">
                  {section.title}
                </a>
              )) : <p className="text-sm text-muted-foreground">{pageCopy.overview}</p>}
            </div>
          </div>


          <div className="rounded-[1.5rem] border border-[#BFDADC] bg-white/92 p-5 shadow-[0_14px_35px_rgba(8,22,51,0.08)]">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{pageCopy.related}</p>
            <div className="mt-4 space-y-3">
              {relatedArticles.map((item) => (
                <Link key={item.slug} to={localizedLearningArticleUrl({ lang, category: topic, article: item })} className="block rounded-2xl border border-border bg-background/70 p-4 transition hover:border-primary/50 hover:shadow-lg">
                  <p className="text-xs font-black uppercase tracking-widest text-primary">{item.subtopic || topicData.h1}</p>
                  <h3 className="mt-2 font-black leading-tight">{getLocalizedLearningArticle(item, lang).title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{getLocalizedLearningArticle(item, lang).excerpt}</p>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-[#BFDADC] bg-white/92 p-5 shadow-[0_14px_35px_rgba(8,22,51,0.08)]">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{pageCopy.keepLearning}</p>
            <h2 className="mt-2 text-xl font-black">{pageCopy.exploreTopic(topicData.h1)}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{pageCopy.keepLearningText}</p>
            <Button asChild className="mt-4 w-full"><Link to={`${prefix}/learning-center/${topic}`}>{pageCopy.openTopic} <ArrowRight className="h-4 w-4" /></Link></Button>
          </div>
        </aside>
      </div>
      </div>
    </article>
  );
}